import { action, computed, makeObservable, observable } from 'mobx';
import type { DeleteFieldConfirmation } from '../types/SchemaSource';
import { ReportBI } from '../services';
import { ConditionalModalState } from './ConditionalModalState';
import { FieldOperationsBIReporter } from './FieldOperationsBIReporter';
import { FieldArchive, FieldManagement } from '../types/FieldsSource';

export interface DeleteFieldModalStateParams {
  readonly fieldManagement: Pick<FieldManagement, 'deleteField'>;
  readonly archive: Pick<FieldArchive, 'items' | 'closePanel'>;
  readonly reportBi: ReportBI;
}

export class DeleteFieldModalState {
  readonly biReporter;
  readonly fieldManagement;
  readonly archive;
  readonly modal;
  acknowledged = false;

  constructor(params: DeleteFieldModalStateParams) {
    this.biReporter = new FieldOperationsBIReporter({
      reportBi: params.reportBi,
    });
    this.fieldManagement = params.fieldManagement;
    this.archive = params.archive;
    this.modal = new ConditionalModalState<{
      id: string;
      origin: string;
      fieldName: string;
      confirmation?: DeleteFieldConfirmation;
    }>({
      canConfirm: (): boolean => this.canConfirm,
      onConfirm: async () => {
        if (!this.modal.value) {
          return;
        }

        this.biReporter.reportDeleteFieldModalCtaClicked('Delete');
        await this.fieldManagement.deleteField(
          this.modal.value.id,
          this.modal.value.origin,
        );

        // Close archived panel if no items left
        if (this.archive.items.length === 0) {
          this.archive.closePanel();
        }
      },
      onClose: () => {
        this.biReporter.reportDeleteFieldModalCtaClicked('Cancel');
      },
    });
    makeObservable(this, {
      acknowledged: observable,
      canConfirm: computed,
      setAcknowledged: action.bound,
    });
    this.modal.modal.events.on('open', () => this.setAcknowledged(false));
  }

  get canConfirm(): boolean {
    return (
      this.modal.isOpen &&
      !this.modal.modal.confirming.isLoading &&
      (!this.modal.value?.confirmation?.confirmationCheckboxLabel ||
        this.acknowledged)
    );
  }

  setAcknowledged(acknowledged: boolean) {
    this.acknowledged = acknowledged;
  }
}
